import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { 
  loadAllRacePlans, 
  saveRacePlan, 
  deleteRacePlanWithCloud,
  savePlanToCloud,
  loadPlanFromCloud,
  loadAuthenticatedPlans
} from '../utils/storage';
import { calculateNutritionPlan, calculatePaces } from '../utils/calculations';
import { useAuth } from './AuthContext';

/**
 * PlanContext
 * 
 * Manages race plans throughout the application.
 * Handles loading, saving, deleting, and current plan state.
 * 
 * Usage:
 * const { plans, currentPlan, loadPlans, savePlan, deletePlan } = usePlans();
 */

const PlanContext = createContext();

export const PlanProvider = ({ children }) => {
  const { user, getIdToken } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentFormData, setCurrentFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load all plans (local + cloud or authenticated)
   */
  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (user && getIdToken) {
        // User is authenticated - load from cloud
        console.log('PlanContext: Loading authenticated plans from cloud');
        const cloudPlans = await loadAuthenticatedPlans(getIdToken);
        console.log(`PlanContext: Loaded ${cloudPlans.length} authenticated plans`);
        setPlans(cloudPlans);
        return cloudPlans;
      } else {
        // User is anonymous - load from localStorage + cloud refs
        console.log('PlanContext: Loading anonymous plans (local + cloud refs)');
        const allPlans = await loadAllRacePlans();
        console.log(`PlanContext: Loaded ${allPlans.length} anonymous plans`);
        setPlans(allPlans);
        return allPlans;
      }
    } catch (err) {
      console.error('PlanContext: Error loading plans:', err);
      setError(err.message);
      setPlans([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, getIdToken]);

  // Load all plans on mount and when user changes (login/logout)
  useEffect(() => {
    loadPlans();
  }, [loadPlans, user]);

  /**
   * Calculate and set results for a plan
   */
  const calculatePlan = useCallback((formData) => {
    try {
      console.log('PlanContext: Calculating plan');
      
      // Ensure distance is numeric
      const numericFormData = {
        ...formData,
        distance: parseFloat(formData.distance)
      };
      
      // Calculate nutrition plan
      const nutritionPlan = calculateNutritionPlan(numericFormData);
      
      const paceModel = numericFormData.paceModel || 'even';
      const paces = calculatePaces(numericFormData, paceModel);
      
      // Create results object
      const results = {
        formData: numericFormData,
        nutritionPlan,
        paces,
        id: formData.id || `plan_${Date.now()}`,
        timestamp: new Date().toISOString()
      };
      
      console.log('PlanContext: Plan calculated successfully');
      setCurrentPlan(results);
      setCurrentFormData(numericFormData);
      
      return results;
    } catch (err) {
      console.error('PlanContext: Error calculating plan:', err);
      setError(err.message);
      return null;
    }
  }, []);

  /**
   * Save a plan locally
   */
  const savePlanLocally = useCallback(async (plan) => {
    try {
      console.log('PlanContext: Saving plan locally');
      const success = saveRacePlan(plan);
      
      if (success) {
        await loadPlans(); // Refresh plans list
        console.log('PlanContext: Plan saved locally');
        return true;
      }
      
      throw new Error('Failed to save plan locally');
    } catch (err) {
      console.error('PlanContext: Error saving plan locally:', err);
      throw err;
    }
  }, [loadPlans]);

  /**
   * Save a plan to cloud
   */
  const savePlanToCloudStorage = useCallback(async (plan, overwrite = false, planIdToReplace = null) => {
    try {
      console.log('PlanContext: Saving plan to cloud');
      const cloudUrl = await savePlanToCloud(plan, overwrite, planIdToReplace, user ? getIdToken : null);
      
      if (cloudUrl) {
        await loadPlans(); // Refresh plans list
        console.log('PlanContext: Plan saved to cloud');
        return cloudUrl;
      }
      
      throw new Error('Failed to save plan to cloud');
    } catch (err) {
      console.error('PlanContext: Error saving plan to cloud:', err);
      throw err;
    }
  }, [loadPlans, user, getIdToken]);

  /**
   * Delete a plan
   */
  const deletePlan = useCallback(async (planId) => {
    try {
      console.log('PlanContext: Deleting plan:', planId);
      const success = await deleteRacePlanWithCloud(planId, user ? getIdToken : null);
      
      if (success) {
        await loadPlans(); // Refresh plans list
        
        // If deleted plan is current, clear it
        if (currentPlan?.id === planId) {
          setCurrentPlan(null);
          setCurrentFormData(null);
        }
        
        console.log('PlanContext: Plan deleted');
        return true;
      }
      
      throw new Error('Failed to delete plan');
    } catch (err) {
      console.error('PlanContext: Error deleting plan:', err);
      throw err;
    }
  }, [loadPlans, currentPlan, user, getIdToken]);

  /**
   * Load a specific plan
   */
  const loadPlan = useCallback((plan) => {
    console.log('PlanContext: Loading plan:', plan.id);
    setCurrentPlan(plan);
    setCurrentFormData(plan.formData);
  }, []);

  /**
   * Clear current plan (results only, keeps form data)
   */
  const clearCurrentPlan = useCallback(() => {
    console.log('PlanContext: Clearing current plan results');
    setCurrentPlan(null);
    // Note: Does NOT clear currentFormData - that's intentional for editing
  }, []);
  
  /**
   * Clear everything (plan results AND form data)
   */
  const clearEverything = useCallback(() => {
    console.log('PlanContext: Clearing everything');
    setCurrentPlan(null);
    setCurrentFormData(null);
  }, []);

  const value = {
    // State
    plans,
    currentPlan,
    currentFormData,
    loading,
    error,
    
    // Actions
    loadPlans,
    calculatePlan,
    savePlanLocally,
    savePlanToCloudStorage,
    deletePlan,
    loadPlan,
    clearCurrentPlan,
    clearEverything,
    setCurrentFormData,
    setCurrentPlan,
    
    // Convenience getters
    get hasPlan() {
      return currentPlan !== null;
    },
    
    get planCount() {
      return plans.length;
    }
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

export const usePlans = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlans must be used within PlanProvider');
  }
  return context;
};

export default PlanContext;

